// Validation & reconciliation — PSD §4.2.8 (F12.2). Engine layer (framework-free).
import { fullyDilutedShares } from './fullyDiluted';
import { RECONCILE_TOLERANCE_USD, ANCHOR_STALE_DAYS } from './constants';
import { daysBetween } from './dates';
/** Do allocations sum to V within the reconciliation tolerance? */
export function reconciled(allocation, V) {
    const sum = allocation.reduce((s, r) => s + r.trancheValue, 0);
    return Math.abs(sum - V) <= RECONCILE_TOLERANCE_USD;
}
/**
 * Validate the valuation. P1 subset of codes (PSD §4.2.8); full catalogue added in Phase 2.
 */
export function validate(inputs, allocation, headline, _bps) {
    const issues = [];
    const cap = inputs.capTableSnapshot;
    // EMPTY_CAP_TABLE
    if (fullyDilutedShares(cap) === 0) {
        issues.push({
            severity: 'error',
            code: 'EMPTY_CAP_TABLE',
            message: 'The cap table has no dilutive shares.',
            instrumentId: null,
        });
    }
    // AUTH_LT_OUTSTANDING
    for (const inst of cap.instruments) {
        if (inst.sharesAuthorized < inst.sharesOutstanding) {
            issues.push({
                severity: 'error',
                code: 'AUTH_LT_OUTSTANDING',
                message: `Instrument "${inst.name}" authorized < outstanding.`,
                instrumentId: inst.id,
            });
        }
    }
    // NEGATIVE_ALLOCATION
    for (const r of allocation) {
        if (r.trancheValue < -1e-9 * headline.totalEquityValue) {
            issues.push({
                severity: 'error',
                code: 'NEGATIVE_ALLOCATION',
                message: `Tranche ${r.breakpointIndex} has a negative allocation.`,
                instrumentId: null,
            });
        }
    }
    // RECONCILIATION_FAILURE
    if (!reconciled(allocation, headline.totalEquityValue)) {
        issues.push({
            severity: 'error',
            code: 'RECONCILIATION_FAILURE',
            message: 'Allocations do not reconcile to total equity value within ±$0.01.',
            instrumentId: null,
        });
    }
    // MISSING_PREFERENCE_TERMS
    for (const inst of cap.instruments) {
        if (inst.kind === 'preferred' && (inst.liquidationMultiple === null || inst.liquidationMultiple <= 0)) {
            issues.push({
                severity: 'warning',
                code: 'MISSING_PREFERENCE_TERMS',
                message: `Preferred "${inst.name}" has no liquidation preference terms.`,
                instrumentId: inst.id,
            });
        }
    }
    // ANCHOR_STALE
    if (inputs.equityValue.transactionDate && inputs.compliance.valuationDate) {
        const age = daysBetween(inputs.equityValue.transactionDate, inputs.compliance.valuationDate);
        if (age > ANCHOR_STALE_DAYS) {
            issues.push({
                severity: 'warning',
                code: 'ANCHOR_STALE',
                message: `Anchor transaction is ${age} days old (> ${ANCHOR_STALE_DAYS}).`,
                instrumentId: inputs.equityValue.anchorInstrumentId,
            });
        }
    }
    // ANCHOR_NOT_ARMS_LENGTH
    if (!inputs.equityValue.armLengthFlag) {
        issues.push({
            severity: 'warning',
            code: 'ANCHOR_NOT_ARMS_LENGTH',
            message: 'Anchor transaction flagged as not arm\u2019s-length.',
            instrumentId: inputs.equityValue.anchorInstrumentId,
        });
    }
    // ---- Phase 2 additions (full catalogue) ----
    // MISSING_CONVERSION_RATIO
    for (const inst of cap.instruments) {
        if (inst.kind === 'preferred' && (inst.conversionRatio === null || inst.conversionRatio <= 0)) {
            issues.push({
                severity: 'warning',
                code: 'MISSING_CONVERSION_RATIO',
                message: `Preferred "${inst.name}" has no conversion ratio; using 1.0.`,
                instrumentId: inst.id,
            });
        }
    }
    // MISSING_RIGHTS — preferred missing seniority
    for (const inst of cap.instruments) {
        if (inst.kind === 'preferred' && (inst.seniority === null || inst.seniority < 0)) {
            issues.push({
                severity: 'warning',
                code: 'MISSING_RIGHTS',
                message: `Preferred "${inst.name}" has no seniority tier assigned.`,
                instrumentId: inst.id,
            });
        }
    }
    // INCONSISTENT_COUNTS — options/warrants with no strike
    for (const inst of cap.instruments) {
        if ((inst.kind === 'option' || inst.kind === 'warrant') && (inst.strikePrice === null || inst.strikePrice < 0)) {
            issues.push({
                severity: 'warning',
                code: 'INCONSISTENT_COUNTS',
                message: `${inst.kind} "${inst.name}" has no strike price.`,
                instrumentId: inst.id,
            });
        }
    }
    // IMPOSSIBLE_WATERFALL — preferred liquidation multiple negative
    for (const inst of cap.instruments) {
        if (inst.kind === 'preferred' && inst.liquidationMultiple !== null && inst.liquidationMultiple < 0) {
            issues.push({
                severity: 'error',
                code: 'IMPOSSIBLE_WATERFALL',
                message: `Preferred "${inst.name}" has a negative liquidation multiple.`,
                instrumentId: inst.id,
            });
        }
    }
    // GRANT_DATE_STALE
    if (inputs.compliance.grantDate && inputs.compliance.valuationDate) {
        const gap = daysBetween(inputs.compliance.valuationDate, inputs.compliance.grantDate);
        if (gap > 365) {
            issues.push({
                severity: 'warning',
                code: 'GRANT_DATE_STALE',
                message: `Grant date is ${gap} days after valuation (> 12 months).`,
                instrumentId: null,
            });
        }
    }
    // FMV_BELOW_MINIMUM
    if (headline.commonFmvPerShare > 0 && headline.commonFmvPerShare < 0.0001) {
        issues.push({
            severity: 'warning',
            code: 'FMV_BELOW_MINIMUM',
            message: 'FMV per share is below the $0.0001 minimum; clamped.',
            instrumentId: null,
        });
    }
    // VOLATILITY_OUTLIER (Emergent E3.1)
    const vol = inputs.assumptions.volatility;
    if (vol < 0.2 || vol > 0.8) {
        issues.push({
            severity: 'warning',
            code: 'VOLATILITY_OUTLIER',
            message: `Volatility of ${(vol * 100).toFixed(0)}% is outside the typical [20%, 80%] range — verify.`,
            instrumentId: null,
        });
    }
    return issues;
}
//# sourceMappingURL=validate.js.map